import ToastMessage from "../toastMessage/ToastMessage";

var axios = require("axios");
var portalConfig = require("../config");
var toast = require("react-toastify");

const getUserToken = () => {
  const key = process.env.REACT_APP_URL;

  try {
    return JSON.parse(localStorage.getItem(`userToken_${key}`));
  } catch (error) {
    return null;
  }
};

const isHandlerEnabled = (config = {}) => {
  return Object.prototype.hasOwnProperty.call(config, "handlerEnabled") &&
    !config.handlerEnabled
    ? false
    : true;
};

/**
 * Controls ONLY the global loading indicator.
 *
 * This is intentionally separate from handlerEnabled so authentication,
 * 401/403 handling, etc. are not affected when a request opts out of
 * the global loader.
 */
const shouldShowGlobalLoader = (config = {}) => {
  return config.showGlobalLoader !== false;
};

/**
 * Multiple requests can be active simultaneously.
 *
 * A counter is required because a request finishing must not hide the
 * global loader while another tracked request is still running.
 */
let activeGlobalRequests = 0;

/**
 * Track the exact Axios request configs which incremented the loader.
 * WeakSet keeps this bookkeeping private to the middleware.
 */
const globalLoaderRequests = new WeakSet();

const startGlobalLoader = () => {
  activeGlobalRequests += 1;

  if (activeGlobalRequests === 1) {
    document.body.classList.add("loading-indicator");
  }
};

const stopGlobalLoader = () => {
  activeGlobalRequests = Math.max(0, activeGlobalRequests - 1);

  if (activeGlobalRequests === 0) {
    document.body.classList.remove("loading-indicator");
  }
};

const requestHandler = (request) => {
  /**
   * Authentication behavior stays independent of loader behavior.
   */
  if (isHandlerEnabled(request)) {
    request.headers = request.headers || {};

    const token = getUserToken();

    if (token) {
      request.headers["auth-token"] = token;
    }
  }

  /**
   * Track this request only when it participates in the global loader.
   * Authentication and error handling are still enabled for requests
   * that set showGlobalLoader:false.
   */
  if (shouldShowGlobalLoader(request)) {
    globalLoaderRequests.add(request);
    startGlobalLoader();
  }

  return request;
};

const finalizeGlobalLoader = (config) => {
  if (!config) {
    return;
  }

  if (globalLoaderRequests.delete(config)) {
    stopGlobalLoader();
  }
};

const errorHandler = (error) => {
  finalizeGlobalLoader(error?.config);

  const key = process.env.REACT_APP_URL;
  const config = error?.config || {};
  const status = error?.response?.status;
  const responseData = error?.response?.data;

  const responseMessage =
    typeof responseData === "string"
      ? responseData
      : responseData?.detail ||
        responseData?.message ||
        responseData?.error ||
        "";

  // Log the failing API so we can identify the actual cause.
  console.error("[API request failed]", {
    url: error?.config?.url,
    method: error?.config?.method,
    status,
    message: responseMessage || error?.message,
  });

  if (isHandlerEnabled(config)) {
    const normalizedMessage = String(responseMessage).toLowerCase();

    const isDepartmentResolutionError =
      normalizedMessage.includes(
        "the logged-in user's department could not be determined",
      ) ||
      normalizedMessage.includes(
        "logged-in user's department could not be determined",
      ) ||
      normalizedMessage.includes(
        "department assigned to your account could not be resolved",
      );

    if (status === 401 && isDepartmentResolutionError) {
      // Do not destroy a valid login session for a department-mapping issue.
      toast.toast.error(
        responseMessage ||
          "Your department could not be determined. Please contact IT.",
        { containerId: "toast" },
      );
    } else if (status === 401) {
      // Preserve existing handling for other unauthorized responses.
      localStorage.removeItem("user");
      localStorage.removeItem(`userToken_${key}`);

      window.location.reload();
    } else if (status === 406) {
      toast.toast.error(
        responseMessage || "The request could not be processed.",
        { containerId: "toast" },
      );
    } else if (status === 403) {
      toast.toast.error(
        responseMessage || `${ToastMessage.errorList.forbidden}`,
        { containerId: "toast" },
      );
    } else if (status === 409) {
      // LeaveSheet.jsx handles the API error message.
    } else {
      toast.toast.error(
        responseMessage || error?.message || "Request failed.",
        { containerId: "toast" },
      );
    }
  }

  return Promise.reject(error);
};

const successHandler = (response) => {
  finalizeGlobalLoader(response?.config);

  if (isHandlerEnabled(response?.config)) {
    // Existing success handling intentionally preserved.
  }

  return response;
};

// Init Axios
const instance = axios.create({
  baseURL: portalConfig.APIUrl,
});

// Add interceptors
instance.interceptors.request.use(
  (request) => requestHandler(request),
  (error) => errorHandler(error),
);

instance.interceptors.response.use(
  (response) => successHandler(response),
  (error) => errorHandler(error),
);

export default axios = instance;
